import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { ApiService } from 'src/data/services/Services';
import logError from 'src/data/services/logError';
import { resolveAppUpdate } from 'src/domain/models/AppUpdateDialogType';
import type { ResolvedAppUpdate } from 'src/domain/models/AppUpdateDialogType';

interface AppUpdateState {
	update: ResolvedAppUpdate | null;
	isAppUpdateDialogVisible: boolean;
	// A per-session dismissal of an optional update, so the dialog does not reappear on every foreground.
	optionalUpdateDismissed: boolean;
}

const initialState: AppUpdateState = {
	update: null,
	isAppUpdateDialogVisible: false,
	optionalUpdateDismissed: false,
};

export const useAppUpdateStore = create(
	combine(initialState, (set, get) => ({
		setAppUpdateDialogVisible: (visible: boolean) => {
			// Dismissing the dialog for an optional update suppresses it for the session.
			const { update } = get();
			if (!visible && update !== null && !update.should_mandatory_update_app) {
				set({ isAppUpdateDialogVisible: false, optionalUpdateDismissed: true });
				return;
			}
			set({ isAppUpdateDialogVisible: visible });
		},

		fetchSettings: async () => {
			try {
				const { data } = await ApiService.endpoints.settings.get();
				const update = resolveAppUpdate(data);

				if (update.should_mandatory_update_app) {
					set({ update, isAppUpdateDialogVisible: true });
					return;
				}

				if (update.should_update_app && !get().optionalUpdateDismissed) {
					set({ update, isAppUpdateDialogVisible: true });
					return;
				}

				set({ update, isAppUpdateDialogVisible: false });
			} catch (error) {
				// A failed settings fetch is non-fatal: leave the dialog hidden.
				logError(error);
			}
		},
	})),
);

export default useAppUpdateStore;
